// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, agencyFunctions } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [isSupervisor, setIsSupervisor] = useState(false);
    const [userDoc, setUserDoc] = useState(null);
    const [isFired, setIsFired] = useState(false);
    const [pageOverrides, setPageOverrides] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);

            if (!user) {
                setUserRole(null);
                setIsSupervisor(false);
                setUserDoc(null);
                setIsFired(false);
                setPageOverrides({});
                sessionStorage.removeItem('loginActivityRecorded');
                setLoading(false);
                return;
            }

            // ============================
            // Record login activity
            // ============================
            const alreadyRecorded = sessionStorage.getItem('loginActivityRecorded');
            if (!alreadyRecorded) {
                try {
                    const recordLogin = httpsCallable(agencyFunctions, 'recordLoginActivity');
                    await recordLogin({});
                    sessionStorage.setItem('loginActivityRecorded', 'true');
                } catch (error) {
                    console.error("Error calling recordLoginActivity:", error);
                }
            }

            // ============================
            // Real-time user doc listener
            // ============================
            const userDocRef = doc(db, 'users', user.uid);

            const unsubscribeUser = onSnapshot(userDocRef, async (snap) => {
                if (!snap.exists()) {
                    console.warn("User document not found:", user.uid);
                    setUserRole(null);
                    setIsSupervisor(false);
                    setUserDoc(null);
                    setIsFired(false);
                    setPageOverrides({});
                    // Role/overrides are resolved (as "none") — safe to render now.
                    setLoading(false);
                    return;
                }

                const userData = snap.data();
                setUserDoc(userData);
                setUserRole(userData.role || null);
                setPageOverrides(userData.pageOverrides || {});

                // ============================
                // 🔥 Fired detection logic
                // ============================
                const terminated =
                    userData.employmentStatus === 'Terminated' ||
                    userData.active === false;

                setIsFired(terminated);

                // 🚫 إذا الحساب موقوف → تسجيل خروج مباشر
                if (terminated) {
                    console.warn("User account terminated. Logging out...");
                    setLoading(false);
                    await signOut(auth);
                    return;
                }

                // Role + overrides are now populated — stop the global loading gate so
                // ProtectedRoute decides with a real role (prevents a redirect to /dashboard on
                // reload). The supervisor lookup below is non-blocking.
                setLoading(false);

                // ============================
                // Supervisor check
                // ============================
                try {
                    const traineesRef = collection(db, 'trainees');
                    const supervisorQuery = query(
                        traineesRef,
                        where('supervisorIds', 'array-contains', user.uid)
                    );
                    const supervisedTraineesSnap = await getDocs(supervisorQuery);
                    setIsSupervisor(!supervisedTraineesSnap.empty);
                } catch (error) {
                    console.error("Error checking supervisor:", error);
                    setIsSupervisor(false);
                }
            }, (error) => {
                // Don't hang on "Verifying Access…" if the user doc can't be read.
                console.error("User doc listener error:", error);
                setLoading(false);
            });

            return () => unsubscribeUser();
        });

        return () => unsubscribeAuth();
    }, []);

    const value = {
        currentUser,
        userRole,
        isSupervisor,
        userDoc,
        isFired,
        pageOverrides,
        loading,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}
